import { useCallback, useEffect, useRef } from 'react';

/** Quiet window after the last observed interaction during which preview
 *  swaps stay deferred. Exported for tests. */
export const PREVIEW_INTERACTION_QUIET_MS = 10_000;

/**
 * Tracks whether the user is actively interacting with the preview iframe
 * (stability overhaul Phase 5). Two signals, no iframe instrumentation:
 *
 * 1. Focus heuristic (primary): any click/keystroke inside a cross-origin
 *    iframe steals focus from the parent window — the parent fires `blur`
 *    with `document.activeElement` pointing at the iframe. While the iframe
 *    holds focus the user is interacting; when the parent regains focus a
 *    quiet-window timer starts.
 * 2. `noteInteraction()` (secondary): the preview template already posts
 *    LOG_ENTRY messages for clicks/submits — the page bumps the timestamp
 *    from that handler, covering focus-tracking blind spots.
 *
 * Ref-based API (no re-renders): `isInteracting()` is read at swap time by
 * the session hook's `shouldDeferPreviewSwap`.
 */
export function usePreviewInteraction(iframeRef: React.RefObject<HTMLIFrameElement | null>): {
  isInteracting: () => boolean;
  noteInteraction: () => void;
} {
  const lastInteractionAtRef = useRef(0);
  const iframeFocusedRef = useRef(false);

  useEffect(() => {
    const onWindowBlur = () => {
      if (iframeRef.current && document.activeElement === iframeRef.current) {
        iframeFocusedRef.current = true;
        lastInteractionAtRef.current = Date.now();
      }
    };
    const onWindowFocus = () => {
      if (iframeFocusedRef.current) {
        iframeFocusedRef.current = false;
        lastInteractionAtRef.current = Date.now();
      }
    };
    window.addEventListener('blur', onWindowBlur);
    window.addEventListener('focus', onWindowFocus);
    return () => {
      window.removeEventListener('blur', onWindowBlur);
      window.removeEventListener('focus', onWindowFocus);
    };
  }, [iframeRef]);

  const isInteracting = useCallback(
    () =>
      iframeFocusedRef.current ||
      Date.now() - lastInteractionAtRef.current < PREVIEW_INTERACTION_QUIET_MS,
    [],
  );

  const noteInteraction = useCallback(() => {
    lastInteractionAtRef.current = Date.now();
  }, []);

  return { isInteracting, noteInteraction };
}
